import React, { useEffect, useState, useRef } from "react"; // 1. Added useRef
import { Play } from "lucide-react";
import { BsImages, BsPlayCircle } from "react-icons/bs";
import Hero from "../assets/galleryBanner.png";
import api from "../common/apiClient";
import SummaryApi from "../common/SummaryApi";
import CommonCTA from "../components/CommonCTA";

const GalleryPage = () => {
  const [videos, setVideos] = useState([]);
  const [images, setImages] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // 2. Ref for scrolling
  const featuredVideoRef = useRef(null);

  const smallVideos = videos.slice(1);

  // Helper to extract YouTube Video ID
  const getYouTubeID = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]+)/);
    return match ? match[1] : null;
  };

  // 3. Fallback Thumbnail Logic
  const getThumbnail = (video) => {
    if (video.thumbnail) return video.thumbnail;
    const id = getYouTubeID(video.videoUrl);
    return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : "";
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const uploaded = new Date(date);
    const diff = Math.floor((now - uploaded) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Today";
    if (diff === 1) return "1 day ago";
    if (diff < 7) return `${diff} days ago`;
    const weeks = Math.floor(diff / 7);
    if (weeks === 1) return "1 week ago";
    if (weeks < 4) return `${weeks} weeks ago`;
    const months = Math.floor(diff / 30);
    return `${months} month${months > 1 ? "s" : ""} ago`;
  };

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await api({
          url: SummaryApi.getAllGallery.url,
          method: SummaryApi.getAllGallery.method,
        });

        const data = res?.data?.data || [];

        const videoData = data
          .filter((item) => item.type === "VIDEO")
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          
        const imageData = data
          .filter((item) => item.type === "IMAGE")
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          
        const formattedVideos = videoData.map((item) => ({
          id: item._id,
          title: item.title,
          thumbnail: item.thumbnail?.url,
          videoUrl: item.videoUrl,
          createdAt: item.createdAt,
          description: item.description,
          tag: item.tag || "STORY",
        }));

        const formattedImages = imageData.map((item) => ({
          id: item._id,
          image: item.image?.url || item.thumbnail?.url,
          title: item.title,
          tag: item.tag || "Gallery",
        }));

        setVideos(formattedVideos);
        setImages(formattedImages);
        if (formattedVideos.length > 0) {
          setActiveVideo(formattedVideos[0]);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchGallery();
  }, []);

  const getEmbedUrl = (url) => {
    const id = getYouTubeID(url);
    // added autoplay=1 to play immediately when swapped
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : "";
  };

  // 4. Handle Video Change
  const handleVideoSelect = (video) => {
    setActiveVideo(video);

    // Scroll to the featured video section
    featuredVideoRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="bg-[#f5f5f2] min-h-screen">
      {/* HERO SECTION */}
      <div className="w-full h-[70vh] md:h-[75vh] relative overflow-hidden">
        <img src={Hero} alt="banner" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-black/25 flex flex-col items-center justify-center text-white text-center px-4">
          <h1 className="text-5xl md:text-6xl font-times">Gallery</h1>
          <p className="mt-3 max-w-xl text-sm md:text-base">
            Experience the serene beauty of luxury estates nestled in the lush landscapes of Idukki, Kerala.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-12">
        {/* Plantation Story - Ref added here */}
        <div className="mb-10" ref={featuredVideoRef}>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h2 className="text-base text-[#0F172A] flex items-center gap-2">
              <span className="text-[#45A301]"><BsPlayCircle className="w-5 h-5" /></span>
              The Plantation Story
            </h2>
            <a href="https://youtube.com/@klpropertytamil" target="_blank" rel="noopener noreferrer" className="text-sm text-[#45A301] hover:text-[#3e9202]">
              View YouTube Channel
            </a>
          </div>

          {/* Featured Video */}
          {activeVideo && (
            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-black">
              {!isPlaying ? (
                <>
                  <img
                    src={getThumbnail(activeVideo)} // Using helper
                    alt={activeVideo.title}
                    className="w-full h-[260px] md:h-[460px] object-fill"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button onClick={() => setIsPlaying(true)} className="bg-white/20 backdrop-blur-md p-4 rounded-full">
                      <Play className="text-white" />
                    </button>
                  </div>
                </>
              ) : (
                <iframe
                  key={activeVideo.id}
                  src={getEmbedUrl(activeVideo.videoUrl)}
                  title={activeVideo.title}
                  className="w-full h-[260px] md:h-[460px]"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              )}

              {/* Text overlay - hide when playing for better view (Optional) */}
              {!isPlaying && (
                <div className="absolute bottom-6 left-6 text-white pointer-events-none">
                  <span className="text-xs bg-[#45A301] px-3 py-1.5 rounded-full uppercase">{activeVideo.tag}</span>
                  <h3 className="text-xl font-semibold mt-2">{activeVideo.title}</h3>
                  <p className="text-sm opacity-90 max-w-md text-[#FFFFFFB2] mt-1">{activeVideo.description}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Small Videos List */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {smallVideos.map((video, index) => (
            <div
              key={video.id || index}
              className={`group cursor-pointer ${video.id === activeVideo?.id ? "ring-2 ring-[#45A301] rounded-2xl p-1" : ""}`}
              onClick={() => handleVideoSelect(video)} // Using select handler
            >
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={getThumbnail(video)} // Using helper for small thumbnails too
                  alt={video.title}
                  className="w-full h-[220px] object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <div className="mt-3">
                <h3 className="text-sm font-medium text-gray-800 leading-snug">{video.title}</h3>
                <p className="text-xs text-gray-500 mt-1">• {video.createdAt ? getTimeAgo(video.createdAt) : "Recently added"}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ... (rest of images gallery code stays same) */}
        <h2 className="text-base text-[#0F172A] flex items-center gap-2 mb-6">
          <span className="text-[#45A301]"><BsImages className="w-5 h-5" /></span>
          Landscape & Macro Gallery
        </h2>

        <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {images.map((item, index) => (
            <div key={index} className="bg-white border border-[#45A3010D] rounded-xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-lg transition">
              <div className="w-full h-[260px] overflow-hidden">
                <img src={item.image} alt={item.title} className="w-full h-full object-fill hover:scale-105 transition duration-300" />
              </div>
              <div className="px-5 py-6">
                <p className="text-[11px] text-[#6A8E22] font-semibold uppercase tracking-wide mb-1">{item.tag}</p>
                <h3 className="text-[16px] font-medium text-[#1F2937] leading-snug">{item.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
      <CommonCTA />
    </div>
  );
};

export default GalleryPage;